import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi, countriesApi } from '../../../api';
import useUIStore from '../../../store/useUIStore';

const UserDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [countries, setCountries] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    country: '',
    role: '',
    status: 1,
  });
  const { showSuccess, showError } = useUIStore();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setErrorMsg('');
      try {
        const [userResponse, countriesResponse] = await Promise.all([
          adminApi.getUserById(id),
          countriesApi.getCountryCodes(),
        ]);
        const user = userResponse?.data?.data || userResponse?.data || {};
        const countryList = countriesResponse?.data?.data || countriesResponse?.data || [];
        setCountries(Array.isArray(countryList) ? countryList : []);
        setFormData({
          name: user.u_name || '',
          email: user.u_email || '',
          mobile: user.u_mobile || '',
          country: user.u_country || '',
          role: user.u_role || '',
          status: user.u_status ?? 1,
        });
      } catch (error) {
        setErrorMsg(error?.response?.data?.error || 'Failed to load user details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadData();
    }
  }, [id]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await adminApi.updateUser(id, {
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        country: formData.country,
        role: formData.role,
        status: formData.status,
      });
      showSuccess('User updated successfully');
    } catch (error) {
      showError(error?.response?.data?.error || 'Failed to update user');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" mb={2}>
        <Button variant="outlined" startIcon={<ArrowLeft size={16} />} onClick={() => navigate('/admin/users')}>
          Back
        </Button>
        <Typography variant="h5">User Details</Typography>
      </Stack>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              {loading ? (
                <Stack alignItems="center" spacing={2} py={4}>
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    Loading user details...
                  </Typography>
                </Stack>
              ) : (
                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Name"
                        value={formData.name}
                        onChange={handleChange('name')}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange('email')}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Mobile"
                        value={formData.mobile}
                        onChange={handleChange('mobile')}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Country"
                        value={formData.country}
                        onChange={handleChange('country')}
                        fullWidth
                      >
                        <MenuItem value="">Select country</MenuItem>
                        {countries.map((country) => (
                          <MenuItem key={country.c_id} value={country.c_id}>
                            {country.countryName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Role"
                        value={formData.role}
                        onChange={handleChange('role')}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Status"
                        value={formData.status}
                        onChange={handleChange('status')}
                        fullWidth
                      >
                        <MenuItem value={1}>Active</MenuItem>
                        <MenuItem value={0}>Inactive</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>

                  <Stack direction="row" spacing={2} mt={3}>
                    <Button type="submit" variant="contained">
                      Update
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/admin/users')}>
                      Cancel
                    </Button>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notes
              </Typography>
              <Alert severity="info">
                Keep user contact details updated to ensure notifications are delivered.
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserDetailPage;
